import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

// Shape returned by GET /api/employer/session — keeps the UI decoupled from
// the exact key names on the wire. New server fields should be added here too.
export interface EmployerSessionPayload {
    authenticated: boolean;
    companyId?: number;
    companyName?: string;
    logoUrl?: string;
    isVerified?: boolean;
    isApproved?: boolean;
    plan?: string;
    // Server-authoritative candidate-pool access flag. Absent during the free
    // launch (server omits it) → treated as granted for approved employers.
    // Once billing is enforced the server sends the real value. This drives
    // UI show/hide only; the search/contact endpoints are the real gate.
    candidateAccess?: boolean;
    // Scheduled end of the free launch (ISO 8601 UTC), or null/absent while Jobs
    // is free. Drives the countdown banners; access itself is candidateAccess.
    billingStartsAt?: string | null;
}

// One source of truth for "am I signed in as an employer, and what do I know
// about them" — replaces the previous pattern of every employer Vue component
// fetching /api/employer/session independently on mount.
//
// EmployerLayout.vue calls fetchSession() once when it mounts; child routes
// read from this store. clearSession() is called on logout.
export const useEmployerSessionStore = defineStore('employerSession', () => {
    const authenticated = ref(false);
    const companyId = ref<number | null>(null);
    const companyName = ref('');
    const logoUrl = ref('');
    const isVerified = ref(false);
    const isApproved = ref(false);
    const plan = ref('Free');
    // Defaults true so approved employers get the candidate pool during the
    // free launch; the server overrides this once billing is enforced.
    const candidateAccess = ref(true);
    // null = free launch (no scheduled end); otherwise the UTC instant paid access begins.
    const billingStartsAt = ref<string | null>(null);
    const loading = ref(false);
    const loaded = ref(false);

    const canPostListings = computed(() => authenticated.value && isApproved.value);
    const needsApproval = computed(() => authenticated.value && !isApproved.value);
    // Company-level plan gate for BOTH posting and candidate search. Approval is
    // required independent of billing (see plan security note); candidateAccess
    // carries the billing decision from the server (defaults true during free launch).
    const hasActivePlan = computed(
        () => authenticated.value && isApproved.value && candidateAccess.value
    );

    // Free launch is scheduled to end but has not yet: drives the countdown banners.
    const launchEndsAt = computed(() =>
        billingStartsAt.value ? new Date(billingStartsAt.value) : null
    );
    const freeLaunchEnding = computed(() => {
        const d = launchEndsAt.value;
        return d ? d.getTime() > Date.now() : false;
    });
    const daysUntilLaunch = computed(() => {
        const d = launchEndsAt.value;
        if (!d) return null;
        const ms = d.getTime() - Date.now();
        return ms > 0 ? Math.ceil(ms / 86_400_000) : 0;
    });
    // Jobs is still free: no end scheduled, or the scheduled end is in the future.
    // Drives whether the "free during launch" banners show at all.
    const freeLaunchActive = computed(() => !billingStartsAt.value || freeLaunchEnding.value);

    async function fetchSession(basePath = ''): Promise<EmployerSessionPayload> {
        loading.value = true;
        try {
            const res = await fetch(`${basePath}/api/employer/session`, {
                credentials: 'same-origin',
                headers: { 'Accept': 'application/json' },
            });
            if (!res.ok) {
                applyPayload({ authenticated: false });
                return { authenticated: false };
            }
            const data = (await res.json()) as EmployerSessionPayload;
            applyPayload(data);
            return data;
        } finally {
            loading.value = false;
            loaded.value = true;
        }
    }

    function applyPayload(p: EmployerSessionPayload) {
        authenticated.value = !!p.authenticated;
        companyId.value = p.companyId ?? null;
        companyName.value = p.companyName ?? '';
        logoUrl.value = p.logoUrl ?? '';
        isVerified.value = !!p.isVerified;
        isApproved.value = !!p.isApproved;
        plan.value = p.plan ?? 'Free';
        candidateAccess.value = p.candidateAccess ?? true;
        billingStartsAt.value = p.billingStartsAt ?? null;
    }

    function clearSession() {
        applyPayload({ authenticated: false });
        loaded.value = true;
    }

    return {
        // state
        authenticated,
        companyId,
        companyName,
        logoUrl,
        isVerified,
        isApproved,
        plan,
        candidateAccess,
        billingStartsAt,
        loading,
        loaded,
        // getters
        canPostListings,
        needsApproval,
        hasActivePlan,
        launchEndsAt,
        freeLaunchEnding,
        daysUntilLaunch,
        freeLaunchActive,
        // actions
        fetchSession,
        clearSession,
    };
});
